import { AudioPlay } from "kaplay";
import { CAMERA, GAME, PHYSICS, PLAYER } from "./constants";
import { getTerrainSlope, getTerrainY } from "./terrain";
import type { Camera, Player, TerrainPoint } from "./types";

export function applyGravity(player: Player, dt: number): void {
  if (!player.onGround) {
    player.vy += PHYSICS.GRAVITY * dt;
  }
}

export function applyDamping(player: Player, dt: number): void {
  const dampingFactor = player.onGround
    ? PLAYER.LINEAR_DAMPING * PLAYER.GROUND_DAMPING_FACTOR
    : PLAYER.LINEAR_DAMPING;

  const damping = (1 - dampingFactor) ** dt;
  player.vx *= damping;
  player.vy *= damping;
}

export function applyDiveForce(
  player: Player,
  isDiving: boolean,
  dt: number,
): void {
  if (!isDiving) return;

  if (player.onGround) {
    player.vx += PLAYER.DIVE_FORCE * PLAYER.DIVE_FORCE_GROUND_FACTOR * dt;
  } else {
    player.vy += PLAYER.DIVE_FORCE * PLAYER.DIVE_FORCE_AIR_FACTOR * dt;
  }
}

export function updatePlayerPosition(player: Player, dt: number): void {
  player.x += player.vx * PHYSICS.PTM_RATIO * dt;
  player.y += player.vy * PHYSICS.PTM_RATIO * dt;
}

export function handleTerrainCollision(
  player: Player,
  terrain: TerrainPoint[],
  camera: Camera,
  dt: number,
  hurt: AudioPlay,
  customPlayerRadius: number = PLAYER.RADIUS,
): boolean {
  const terrainY = getTerrainY(player.x, terrain);
  const playerBottom = player.y + customPlayerRadius * PHYSICS.PTM_RATIO;

  if (playerBottom >= terrainY) {
    player.y = terrainY - customPlayerRadius * PHYSICS.PTM_RATIO;

    if (!player.onGround) {
      const wasGoodLanding = handleLanding(player, terrain, camera, hurt);
      player.onGround = true;
      return wasGoodLanding;
    } else {
      handleGroundMovement(player, terrain, dt);
      return false;
    }
  } else {
    player.onGround = false;
    return false;
  }
}

export function handleLanding(
  player: Player,
  terrain: TerrainPoint[],
  camera: Camera,
  hurt: AudioPlay,
): boolean {
  const slope = getTerrainSlope(player.x, terrain);
  const speed = Math.hypot(player.vx, player.vy);

  const landingAngle = Math.atan2(player.vy, player.vx);
  const slopeAngle = Math.atan(slope);
  const angleDiff = Math.abs(landingAngle - slopeAngle);

  const speedRetention =
    angleDiff < GAME.LANDING_ANGLE_THRESHOLD
      ? GAME.GOOD_LANDING_SPEED_RETENTION
      : GAME.BAD_LANDING_SPEED_RETENTION;

  if (speedRetention === GAME.BAD_LANDING_SPEED_RETENTION) {
    hurt.play();
    const shakeDuration = speed * CAMERA.SHAKE_DURATION_MULTIPLIER;
    camera.shakeTime = shakeDuration;
    camera.initialShakeDuration = shakeDuration;
    camera.shakeIntensity = speed * CAMERA.SHAKE_SPEED_MULTIPLIER;
  }

  const newSpeed = speed * speedRetention;
  player.vx = newSpeed * Math.cos(slopeAngle);
  player.vy = newSpeed * Math.sin(slopeAngle);

  return speedRetention === GAME.GOOD_LANDING_SPEED_RETENTION;
}

export function handleGroundMovement(
  player: Player,
  terrain: TerrainPoint[],
  dt: number,
): void {
  const slope = getTerrainSlope(player.x, terrain);
  const gravityAlongSlope = PHYSICS.GRAVITY * slope * dt;

  player.vx += gravityAlongSlope;
  player.vy = player.vx * slope;
}

export function applyVelocityConstraints(player: Player): void {
  player.vx = Math.max(player.vx, PLAYER.MIN_VELOCITY_X);
  player.vy = Math.min(player.vy, PLAYER.MAX_VELOCITY_Y);
}

export function updatePlayerAngle(player: Player): void {
  player.angle = Math.atan2(player.vy, player.vx);
}

export function updateTrail(player: Player, verticalOffset = 0): void {
  player.trail.push({ x: player.x, y: player.y + verticalOffset, alpha: 1.0 });

  if (player.trail.length > GAME.TRAIL_LENGTH) {
    player.trail.shift();
  }

  for (const [index, point] of player.trail.entries()) {
    point.alpha = index / player.trail.length;
  }
}
